import type { Achievement, ApiResponse, UnitStudent, User } from '@/types';
import { useDemoStore } from '@/stores/demoStore';

type Handler = (body: any) => ApiResponse<any>;

const state = () => useDemoStore.getState().data!;
const mutate = (fn: (draft: ReturnType<typeof state>) => void) => useDemoStore.getState().updateData(fn);
const ok = <T,>(data: T): ApiResponse<T> => ({ success: true, data });
const fail = (error: string): ApiResponse<any> => ({ success: false, data: null, error });
const uuid = (prefix = 'demo') => `${prefix}-${Math.random().toString(36).slice(2, 10)}`;
const nowIso = () => new Date().toISOString();
const relativeTime = (iso: string) => {
  const mins = Math.max(1, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
};
const emailRegex = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const seededNum = (seed: string, min: number, max: number) => {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  const ratio = Math.abs(Math.sin(hash)) % 1;
  return Math.floor(min + ratio * (max - min));
};

const getMyUnits = () => state().units.filter((unit) => (state().unitTeacherIds[unit.id] || []).includes(state().currentUser.id));
const getStudentById = (studentId: string) => state().students.find((student) => student.id === studentId);
const getTeacherById = (teacherId: string) => state().teachers.find((teacher) => teacher.id === teacherId);
const getGameById = (gameId: string) => state().games.find((game) => game.id === gameId);
const getAchievementById = (achievementId: string) => state().achievements.find((achievement) => achievement.id === achievementId);
const studentName = (id: string) => {
  const student = getStudentById(id);
  return student ? `${student.firstName} ${student.lastName}` : 'Unknown Student';
};

const getStudentPlayingTimes = (userId: string, unitId = '') => state().games.slice(0, 3).map((game) => ({
  gameId: game.id,
  gameTitle: game.title,
  totalPlayingTimeSeconds: seededNum(`${userId}-${unitId}-${game.id}-time`, 18000, 79200),
  sessionCount: seededNum(`${userId}-${unitId}-${game.id}-sessions`, 10, 24),
  lastPlayedAt: new Date(Date.now() - seededNum(`${userId}-${unitId}-${game.id}-last`, 1, 30) * 86400000).toISOString(),
}));

const getStudentsWithStats = () => {
  const myUnitIds = getMyUnits().map((unit) => unit.id);

  return state().students.flatMap((student) => {
    const unitIds = myUnitIds.filter((unitId) =>
      (state().unitStudentIds[unitId] || []).includes(student.id)
    );
    if (unitIds.length === 0) {
      return [];
    }

    const assignmentsCount = state().assignments.filter((assignment) => assignment.studentId === student.id).length;
    const achievementsCount = state().userAchievements.filter((achievement) => achievement.userId === student.id).length;
    const playingTimes = getStudentPlayingTimes(student.id, unitIds[0]);
    const totalPlayingSeconds = playingTimes.reduce((sum, item) => sum + item.totalPlayingTimeSeconds, 0);
    const lastActive = playingTimes.map((item) => item.lastPlayedAt).sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0] ?? student.createdAt;
    return [{ ...student, unitIds, assignmentsCount, achievementsCount, totalPlayingHours: Number((totalPlayingSeconds / 3600).toFixed(1)), lastActive }];
  });
};

const getUnitStudents = (unitId: string): UnitStudent[] => {
  const enrolled: UnitStudent[] = (state().unitStudentIds[unitId] || []).map((studentId, index): UnitStudent => {
    const student = getStudentById(studentId)!;
    const playingTimes = getStudentPlayingTimes(studentId);
    return {
      id: student.id,
      email: student.email,
      firstName: student.firstName,
      lastName: student.lastName,
      role: 'student',
      createdAt: student.createdAt,
      status: 'enrolled' as const,
      totalPlayingHours: Number((playingTimes.reduce((sum, item) => sum + item.totalPlayingTimeSeconds, 0) / 3600).toFixed(1)),
      lastActive: playingTimes.sort((a, b) => new Date(b.lastPlayedAt || 0).getTime() - new Date(a.lastPlayedAt || 0).getTime())[0]?.lastPlayedAt ?? new Date(Date.now() - (index + 1) * 86400000).toISOString(),
    };
  });
  const pending: UnitStudent[] = state().pendingEnrollments.filter((item) => item.unitId === unitId).map((item): UnitStudent => ({ id: item.id, email: item.email, firstName: null, lastName: null, role: 'student' as const, createdAt: item.createdAt, status: 'pending' as const, totalPlayingHours: 0, lastActive: null }));
  return [...enrolled, ...pending];
};

const getTeacherDashboardOverview = () => {
  const unitIds = new Set(getMyUnits().map((unit) => unit.id));
  const studentIds = new Set(Array.from(unitIds).flatMap((unitId) => state().unitStudentIds[unitId] || []));
  const gameIds = new Set(Array.from(unitIds).flatMap((unitId) => state().unitGameIds[unitId] || []));
  const relevantAssignments = state().assignments.filter((assignment) => studentIds.has(assignment.studentId));
  const totalPlayingMinutes = Array.from(studentIds).reduce((sum, studentId) => sum + Math.round(getStudentPlayingTimes(studentId).reduce((inner, item) => inner + item.totalPlayingTimeSeconds, 0) / 60), 0);
  const totalInstallCount = Array.from(unitIds).reduce((sum, unitId) => {
    const enrolled = (state().unitStudentIds[unitId] || []).length;
    return sum + (state().unitGameIds[unitId] || []).reduce((gameSum, gameId) => gameSum + enrolled + seededNum(`${unitId}-${gameId}-installs`, Math.floor(enrolled / 2), enrolled), 0);
  }, 0);
  const totalAchievementsUnlocked = state().userAchievements.filter((achievement) => studentIds.has(achievement.userId)).length;
  return { totalStudents: studentIds.size, totalGames: gameIds.size, totalGamesAssigned: relevantAssignments.length, totalPlayingMinutes, totalInstallCount, totalAchievementsUnlocked };
};

const getTeacherGamesOverview = () => {
  const gameIds = Array.from(new Set(getMyUnits().flatMap((unit) => state().unitGameIds[unit.id] || [])));
  return gameIds.map((gameId) => ({
    gameId,
    achievements: state().achievements.filter((achievement) => achievement.gameId === gameId).length,
    uniqueStudentCount: new Set(state().assignments.filter((assignment) => assignment.gameId === gameId).map((assignment) => assignment.studentId)).size,
    unitCount: getMyUnits().filter((unit) => (state().unitGameIds[unit.id] || []).includes(gameId)).length,
  }));
};

const getTeacherAchievementOverview = () => {
  const myStudentIds = new Set(getMyUnits().flatMap((unit) => state().unitStudentIds[unit.id] || []));
  const achievements = state().achievements.map((achievement) => ({ ...achievement, unlockCount: state().userAchievements.filter((item) => item.userId && myStudentIds.has(item.userId) && item.achievementId === achievement.achievementId).length }));
  return { totalStudents: myStudentIds.size, totalUnlocked: state().userAchievements.filter((item) => myStudentIds.has(item.userId)).length, achievements };
};

const getRecentActivity = () => {
  const myStudentIds = new Set(getMyUnits().flatMap((unit) => state().unitStudentIds[unit.id] || []));
  const unlocks = state().userAchievements
    .filter((item) => item.userId && myStudentIds.has(item.userId) && item.achievement)
    .map((item) => ({ student: studentName(item.userId), action: `unlocked "${item.achievement!.name}"`, game: item.gameTitle, at: item.unlockedAt }));
  const submissions = state().activityResponses
    .filter((response) => myStudentIds.has(response.studentId))
    .map((response) => {
      const activity = state().activities.find((item) => item.id === response.activityId);
      return { student: studentName(response.studentId), action: `submitted "${activity?.title ?? 'an activity'}"`, game: activity ? getGameById(activity.gameId)?.title ?? '' : '', at: response.submittedAt };
    });
  return [...unlocks, ...submissions]
    .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime())
    .slice(0, 6)
    .map(({ student, action, game, at }) => ({ student, action, game, time: relativeTime(at) }));
};

const getHydratedActivities = (unitId: string) => state().activities.filter((activity) => activity.unitId === unitId).map((activity) => ({
  ...activity,
  game: getGameById(activity.gameId),
  achievement: getAchievementById(activity.achievementId) as Achievement | undefined,
  responses: state().activityResponses.filter((response) => response.activityId === activity.id),
}));

const getActivityLibrary = () => state().activityLibraryItems.filter((item) => !item.isArchived && (item.createdBy === state().currentUser.id || item.visibility !== 'private')).map((item) => {
  const creator = getTeacherById(item.createdBy) ?? state().currentUser;

  return {
    ...item,
    game: getGameById(item.gameId),
    achievement: getAchievementById(item.achievementId) as Achievement | undefined,
    creator: creator ? {
      id: creator.id,
      email: creator.email,
      firstName: creator.firstName,
      lastName: creator.lastName,
    } : undefined,
  };
}).sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());

const handlers: Record<string, Handler> = {
  '/auth/me': () => ok(state().currentUser),
  '/auth/validate': () => ok({ valid: true, user: state().currentUser }),
  '/auth/logout': () => ok(null),
  '/auth/update-name': ({ firstName, lastName, orgUsername }: { firstName: string; lastName: string; orgUsername?: string }) => {
    mutate((draft) => {
      draft.currentUser.firstName = firstName;
      draft.currentUser.lastName = lastName;
      if (orgUsername !== undefined) draft.currentUser.orgUsername = orgUsername;
      draft.teachers = draft.teachers.map((teacher) => teacher.id === draft.currentUser.id ? { ...teacher, firstName, lastName, orgUsername: orgUsername ?? teacher.orgUsername } : teacher);
    });
    return ok({ user: state().currentUser });
  },

  '/games/list': () => ok(state().games),
  '/games/get': ({ gameId }) => ok(getGameById(gameId) ?? null),
  '/games/create': (body) => {
    const game = { id: uuid('game'), title: '', description: '', publisher: 'Demo Publisher', version: '1.0.0', status: 'draft' as const, executablePath: 'DemoGame.exe', isPublished: false, createdAt: nowIso(), updatedAt: nowIso(), ...body };
    mutate((draft) => { draft.games.push(game); });
    return ok(game);
  },
  '/games/update': ({ gameId, ...patch }) => {
    let updated = null;
    mutate((draft) => { draft.games = draft.games.map((game) => game.id === gameId ? (updated = { ...game, ...patch, updatedAt: nowIso() }) : game); });
    return ok(updated);
  },
  '/games/delete': ({ gameId }) => { mutate((draft) => { draft.games = draft.games.filter((game) => game.id !== gameId); Object.keys(draft.unitGameIds).forEach((unitId) => { draft.unitGameIds[unitId] = (draft.unitGameIds[unitId] || []).filter((id) => id !== gameId); }); }); return ok(null); },

  '/achievements/game-achievements': ({ gameId }) => ok(state().achievements.filter((achievement) => achievement.gameId === gameId)),
  '/achievements/user-achievements': ({ userId, gameId }) => ok(state().userAchievements.filter((achievement) => achievement.userId === userId && achievement.gameId === gameId)),
  '/achievements/all-user-achievements': ({ userId }) => ok(state().userAchievements.filter((achievement) => achievement.userId === userId)),
  '/achievements/teacher-overview': () => ok(getTeacherAchievementOverview()),
  '/achievements/create': (body) => {
    const achievement = { id: uuid('achievement'), achievementId: uuid('external'), points: 10, isSecret: false, createdAt: nowIso(), ...body };
    mutate((draft) => { draft.achievements.push(achievement); });
    return ok(achievement);
  },

  '/assignments/create': (body) => {
    const assignment = { id: uuid('assignment'), assignedBy: state().currentUser.id, createdAt: nowIso(), updatedAt: nowIso(), ...body, game: getGameById(body.gameId), student: getStudentById(body.studentId) };
    mutate((draft) => { draft.assignments.push(assignment); });
    return ok(assignment);
  },
  '/assignments/student-games': ({ studentId }) => ok(state().assignments.filter((assignment) => assignment.studentId === studentId)),
  '/assignments/update': ({ assignmentId, ...patch }) => {
    let updated = null;
    mutate((draft) => { draft.assignments = draft.assignments.map((assignment) => assignment.id === assignmentId ? (updated = { ...assignment, ...patch, updatedAt: nowIso() }) : assignment); });
    return ok(updated);
  },
  '/assignments/delete': ({ assignmentId }) => { mutate((draft) => { draft.assignments = draft.assignments.filter((assignment) => assignment.id !== assignmentId); }); return ok(null); },
  '/assignments/validate-access': ({ studentId, gameId }) => { const assignment = state().assignments.find((item) => item.studentId === studentId && item.gameId === gameId && item.isActive); return ok({ hasAccess: Boolean(assignment), assignment }); },

  '/users/list-students': () => ok(getStudentsWithStats()), // scope: 'units' accepted; demo data is already unit-scoped

  '/units/my-units': () => ok(getMyUnits()),
  '/units/get': ({ unitId }) => ok(state().units.find((unit) => unit.id === unitId) ?? null),
  '/units/create': ({ code, name, year, semester }) => {
    const unit = { id: uuid('unit'), orgId: 'demo-org-0000', code, name, year, semester, isArchived: false, createdAt: nowIso(), updatedAt: nowIso(), organization: { id: 'demo-org-0000', name: 'Ludolio Demo School' } };
    mutate((draft) => {
      draft.units.unshift(unit);
      draft.unitStudentIds[unit.id] = [];
      draft.unitTeacherIds[unit.id] = [draft.currentUser.id];
      draft.unitGameIds[unit.id] = [];
    });
    return ok(unit);
  },
  '/units/delete': ({ unitId, confirmationCode }) => {
    const unit = state().units.find((item) => item.id === unitId);
    if (!unit) return fail('Unit not found');
    if (unit.code !== confirmationCode) return fail('Unit confirmation did not match');

    mutate((draft) => {
      const activityIds = new Set(
        draft.activities.filter((activity) => activity.unitId === unitId).map((activity) => activity.id)
      );
      draft.units = draft.units.filter((item) => item.id !== unitId);
      delete draft.unitStudentIds[unitId];
      delete draft.unitTeacherIds[unitId];
      delete draft.unitGameIds[unitId];
      draft.pendingEnrollments = draft.pendingEnrollments.filter((item) => item.unitId !== unitId);
      draft.pendingTeacherInvites = draft.pendingTeacherInvites.filter((item) => item.unitId !== unitId);
      draft.activities = draft.activities.filter((activity) => activity.unitId !== unitId);
      draft.activityResponses = draft.activityResponses.filter(
        (response) => !activityIds.has(response.activityId)
      );
    });
    return ok({ success: true });
  },
  '/units/students': ({ unitId }) => ok(getUnitStudents(unitId)),
  '/units/teachers': ({ unitId }) => {
    const assigned = ((state().unitTeacherIds[unitId] || []).map((teacherId) => getTeacherById(teacherId)).filter(Boolean) as User[])
      .map((teacher) => ({ ...teacher, status: 'active' as const }));
    const pending = state().pendingTeacherInvites
      .filter((item) => item.unitId === unitId)
      .map((item) => ({
        id: item.id,
        email: item.email,
        firstName: item.email.split('@')[0],
        lastName: null,
        role: 'teacher',
        status: 'pending' as const,
        createdAt: item.createdAt,
      }));
    return ok([...assigned, ...pending]);
  },
  '/units/games': ({ unitId }) => ok(state().games.filter((game) => (state().unitGameIds[unitId] || []).includes(game.id))),
  '/units/add-game': ({ unitId, gameId }) => { mutate((draft) => { const ids = draft.unitGameIds[unitId] || (draft.unitGameIds[unitId] = []); if (!ids.includes(gameId)) ids.push(gameId); }); return ok({ success: true }); },
  '/units/remove-game': ({ unitId, gameId }) => { mutate((draft) => { draft.unitGameIds[unitId] = (draft.unitGameIds[unitId] || []).filter((id) => id !== gameId); }); return ok({ success: true }); },
  '/units/add-student': ({ unitId, studentId }) => { mutate((draft) => { const ids = draft.unitStudentIds[unitId] || (draft.unitStudentIds[unitId] = []); if (!ids.includes(studentId)) ids.push(studentId); }); return ok({ success: true }); },
  '/units/add-students-by-email': ({ unitId, emails }) => {
    const added: any[] = [];
    const pending: string[] = [];
    const alreadyEnrolled: string[] = [];
    const alreadyPending: string[] = [];
    const invalidDomain: string[] = [];
    const invalidFormat: string[] = [];
    mutate((draft) => {
      for (const rawEmail of emails || []) {
        const email = String(rawEmail).trim().toLowerCase();
        if (!emailRegex.test(email)) { invalidFormat.push(email); continue; }
        if (!email.endsWith('@demo-students.ludolio.com')) { invalidDomain.push(email); continue; }
        const existingStudent = draft.students.find((student) => student.email.toLowerCase() === email);
        if (existingStudent) {
          const enrolledIds = draft.unitStudentIds[unitId] || (draft.unitStudentIds[unitId] = []);
          if (enrolledIds.includes(existingStudent.id)) { alreadyEnrolled.push(email); continue; }
          enrolledIds.push(existingStudent.id);
          added.push(existingStudent);
          continue;
        }
        if (draft.pendingEnrollments.some((item) => item.unitId === unitId && item.email.toLowerCase() === email)) { alreadyPending.push(email); continue; }
        draft.pendingEnrollments.push({ id: uuid('pending'), unitId, email, createdAt: nowIso() });
        pending.push(email);
      }
    });
    return ok({
      added,
      pending,
      alreadyEnrolled,
      alreadyPending,
      invalidDomain,
      invalidFormat,
      invitationEmailFailed: [],
      invitationBatchId: uuid('batch'),
      invitationJobsQueued: pending.length,
    });
  },
  '/units/invitation-email-batch-status': ({ batchId }) => ok({
    id: batchId,
    totalJobs: 1,
    completedAt: nowIso(),
    counts: { sent: 1 },
  }),
  '/units/resend-student-invitation': () => ok({ jobId: uuid('job'), status: 'queued', created: true }),
  '/units/remove-student': ({ unitId, studentId }) => { mutate((draft) => { draft.unitStudentIds[unitId] = (draft.unitStudentIds[unitId] || []).filter((id) => id !== studentId); }); return ok({ success: true }); },
  '/units/remove-pending-enrollment': ({ pendingId }) => { mutate((draft) => { draft.pendingEnrollments = draft.pendingEnrollments.filter((item) => item.id !== pendingId); }); return ok({ success: true }); },
  '/units/add-teachers-by-email': ({ unitId, emails }) => {
    const added: any[] = [];
    const pending: string[] = [];
    const alreadyPending: string[] = [];
    const notFound: string[] = [];
    const alreadyAssigned: string[] = [];
    const notTeacher: string[] = [];
    mutate((draft) => {
      for (const rawEmail of emails || []) {
        const email = String(rawEmail).trim().toLowerCase();
        const teacher = draft.teachers.find((item) => item.email.toLowerCase() === email);
        if (teacher) {
          const ids = draft.unitTeacherIds[unitId] || (draft.unitTeacherIds[unitId] = []);
          if (ids.includes(teacher.id)) { alreadyAssigned.push(email); continue; }
          ids.push(teacher.id);
          added.push(teacher);
          continue;
        }
        if (draft.students.some((student) => student.email.toLowerCase() === email)) {
          if (draft.pendingTeacherInvites.some((item) => item.unitId === unitId && item.email === email)) {
            alreadyPending.push(email);
            continue;
          }
          draft.pendingTeacherInvites.push({ id: uuid('pending-teacher'), unitId, email, createdAt: nowIso() });
          pending.push(email);
          continue;
        }
        if (draft.pendingTeacherInvites.some((item) => item.unitId === unitId && item.email === email)) {
          alreadyPending.push(email);
          continue;
        }
        if (!emailRegex.test(email)) {
          notFound.push(email);
          continue;
        }
        draft.pendingTeacherInvites.push({ id: uuid('pending-teacher'), unitId, email, createdAt: nowIso() });
        pending.push(email);
      }
    });
    return ok({ added, pending, alreadyPending, notFound, alreadyAssigned, notTeacher });
  },
  '/units/remove-teacher': ({ unitId, teacherId }) => { mutate((draft) => { draft.unitTeacherIds[unitId] = (draft.unitTeacherIds[unitId] || []).filter((id) => id !== teacherId); }); return ok({ success: true }); },
  '/units/remove-pending-teacher': ({ unitId, pendingId }) => {
    mutate((draft) => {
      draft.pendingTeacherInvites = draft.pendingTeacherInvites.filter((item) => !(item.id === pendingId && item.unitId === unitId));
    });
    return ok({ success: true });
  },
  '/units/resend-pending-teacher-invitation': () => ok({ jobId: uuid('job'), status: 'queued', created: true }),

  '/statistics/teacher-dashboard-overview': () => ok(getTeacherDashboardOverview()),
  '/statistics/recent-activity': () => ok(getRecentActivity()),
  '/statistics/teacher-games-overview': () => ok(getTeacherGamesOverview()),
  '/statistics/unit-statistics': ({ unitId }) => ok((state().unitGameIds[unitId] || []).map((gameId) => {
    const enrolledIds = state().unitStudentIds[unitId] || [];
    const perStudent = enrolledIds.map((studentId) => ({
      time: seededNum(`${studentId}-${gameId}-time`, 18000, 79200),
      sessions: seededNum(`${studentId}-${gameId}-sessions`, 10, 24),
    }));
    return {
      gameId,
      gameTitle: getGameById(gameId)?.title || gameId,
      totalPlayingTimeSeconds: perStudent.reduce((sum, item) => sum + item.time, 0),
      totalSessions: perStudent.reduce((sum, item) => sum + item.sessions, 0),
      uniquePlayersCount: Math.max(1, enrolledIds.length - seededNum(`${unitId}-${gameId}-absent`, 0, 2)),
      installationCount: enrolledIds.length + seededNum(`${unitId}-${gameId}-installs`, Math.floor(enrolledIds.length / 2), enrolledIds.length),
    };
  })),
  '/statistics/unit-game-playing-time': ({ unitId, gameId }) => ok((state().unitStudentIds[unitId] || []).map((studentId, index) => ({ userId: studentId, userName: studentName(studentId), totalPlayingTimeSeconds: seededNum(`${studentId}-${gameId}-time`, 18000, 79200), sessionCount: seededNum(`${studentId}-${gameId}-sessions`, 10, 24), lastPlayedAt: new Date(Date.now() - (index + 1) * 86400000).toISOString() }))),
  '/statistics/unit-game-installations': ({ unitId, gameId }) => ok((state().unitStudentIds[unitId] || []).map((studentId, index) => ({ userId: studentId, userName: studentName(studentId), installationCount: seededNum(`${unitId}-${gameId}-${studentId}-installs`, 1, 4), lastInstalledAt: new Date(Date.now() - (index + 2) * 86400000).toISOString() }))),
  '/statistics/user-playing-times': ({ userId, unitId }) => ok(getStudentPlayingTimes(userId, unitId)),

  '/activities/list': ({ unitId }) => ok(getHydratedActivities(unitId)),
  '/activities/library': () => ok(getActivityLibrary()),
  '/activities/library/create': (body) => {
    const libraryItem = { id: uuid('library'), orgId: 'demo-org-0000', createdAt: nowIso(), updatedAt: nowIso(), createdBy: state().currentUser.id, isArchived: false, visibility: 'private' as const, ...body };
    mutate((draft) => { draft.activityLibraryItems.unshift(libraryItem); });
    return ok(libraryItem);
  },
  '/activities/library/update': ({ libraryItemId, ...patch }) => {
    let updated = null;
    mutate((draft) => { draft.activityLibraryItems = draft.activityLibraryItems.map((item) => item.id === libraryItemId ? (updated = { ...item, ...patch, updatedAt: nowIso() }) : item); });
    return ok(updated);
  },
  '/activities/library/archive': ({ libraryItemId }) => {
    mutate((draft) => { draft.activityLibraryItems = draft.activityLibraryItems.map((item) => item.id === libraryItemId ? { ...item, isArchived: true, updatedAt: nowIso() } : item); });
    return ok({ success: true });
  },
  '/activities/create': (body) => {
    const activity = { id: uuid('activity'), isActive: true, createdAt: nowIso(), updatedAt: nowIso(), createdBy: state().currentUser.id, ...body };
    mutate((draft) => { draft.activities.unshift(activity); });
    return ok(activity);
  },
  '/activities/update': ({ activityId, ...patch }) => {
    let updated = null;
    mutate((draft) => { draft.activities = draft.activities.map((activity) => activity.id === activityId ? (updated = { ...activity, ...patch, updatedAt: nowIso() }) : activity); });
    return ok(updated);
  },
  '/activities/delete': ({ activityId }) => { mutate((draft) => { draft.activities = draft.activities.filter((activity) => activity.id !== activityId); draft.activityResponses = draft.activityResponses.filter((response) => response.activityId !== activityId); }); return ok({ success: true }); },
  '/activities/responses': ({ activityId }) => ok(state().activityResponses.filter((response) => response.activityId === activityId).map((response) => {
    const student = getStudentById(response.studentId);
    return { ...response, student: { id: student?.id, email: student?.email, name: student ? `${student.firstName} ${student.lastName}` : 'Unknown Student' } };
  })),
  '/activities/import': ({ libraryItemId, unitId }) => {
    const source = state().activityLibraryItems.find((item) => item.id === libraryItemId && !item.isArchived);
    if (!source) return fail('Activity library item not found');
    if (!(state().unitTeacherIds[unitId] || []).includes(state().currentUser.id)) return fail('You do not have permission to import into this unit');
    if (!(state().unitGameIds[unitId] || []).includes(source.gameId)) return fail('Add this game to the target unit before importing the activity');
    const activity = { id: uuid('activity'), unitId, gameId: source.gameId, achievementId: source.achievementId, sourceLibraryItemId: source.id, title: source.title, description: source.description, type: source.type, config: source.config, isActive: true, createdBy: state().currentUser.id, createdAt: nowIso(), updatedAt: nowIso() };
    mutate((draft) => { draft.activities.unshift(activity); });
    return ok(activity);
  },
};

export function handleDemoRequest(url: string, body: any): ApiResponse<any> {
  const path = url.replace(/^.*\/api\/v\d+/, '') || url;
  const handler = handlers[path];
  if (!handler) return ok(null);
  return handler(body ?? {});
}